import {
  RichTextDescription,
  RichTextElement
} from 'coop-adventure-engine';
import { sanitizeHtml } from './sanitizer';

const PORTRAIT_VARIANTS = ['thumbnail', 'card', 'large'] as const;
const ICON_VARIANTS = ['small', 'large'] as const;
const DEFAULT_PORTRAIT_VARIANT = 'card';
const DEFAULT_ICON_VARIANT = 'small';

const isPortraitVariant = (value: string): value is typeof PORTRAIT_VARIANTS[number] =>
  PORTRAIT_VARIANTS.includes(value as typeof PORTRAIT_VARIANTS[number]);

const isIconVariant = (value: string): value is typeof ICON_VARIANTS[number] =>
  ICON_VARIANTS.includes(value as typeof ICON_VARIANTS[number]);

/**
 * Renders a RichTextDescription to safe HTML string
 * @param description - The rich text description to render
 * @returns Safe HTML string
 */
export const renderRichText = (description: RichTextDescription): string => {
  if (!description || !description.elements || !Array.isArray(description.elements) || description.elements.length === 0) {
    return '';
  }

  const htmlElements = description.elements.map(element => renderElement(element));
  const html = htmlElements.join('');
  
  // Sanitize the final HTML to prevent XSS attacks
  return sanitizeHtml(html);
};

/**
 * Renders a single RichTextElement to HTML
 * @param element - The rich text element to render
 * @returns HTML string for the element
 */
const renderElement = (element: RichTextElement): string => {
  if (!element || typeof element.type !== 'string') {
    return '';
  }

  switch (element.type) {
    case 'paragraph':
      const paragraphContent = Array.isArray(element.content) 
        ? element.content.map(renderElement).join('')
        : escapeHtml(element.content as string);
      return `<p class="rich-text-paragraph">${paragraphContent}</p>`;

    case 'heading':
      const headingContent = Array.isArray(element.content) 
        ? element.content.map(renderElement).join('')
        : escapeHtml(element.content as string);
      const level = element.level || 1;
      const headingTag = `h${Math.min(Math.max(level, 1), 3)}`;
      return `<${headingTag} class="rich-text-heading rich-text-heading-${level}">${headingContent}</${headingTag}>`;

    case 'list':
      const listItems = Array.isArray(element.content) 
        ? element.content.map(item => {
            if (!item) {
              return '';
            }
            // Handle both string content and array of rich text elements
            const itemContent = Array.isArray(item.content) 
              ? item.content.map(renderElement).join('')
              : escapeHtml(item.content as string);
            return `<li class="rich-text-list-item">${itemContent}</li>`;
          }).join('')
        : '';
      
      const listTag = element.listType === 'numbered' ? 'ol' : 'ul';
      const listClass = `rich-text-list rich-text-list-${element.listType}`;
      return `<${listTag} class="${listClass}">${listItems}</${listTag}>`;

    case 'bold':
      const boldContent = Array.isArray(element.content) 
        ? element.content.map(renderElement).join('')
        : escapeHtml(element.content as string);
      return `<strong class="rich-text-bold">${boldContent}</strong>`;

    case 'italic':
      const italicContent = Array.isArray(element.content) 
        ? element.content.map(renderElement).join('')
        : escapeHtml(element.content as string);
      return `<em class="rich-text-italic">${italicContent}</em>`;

    case 'text':
      return escapeHtml(element.content as string);

    case 'image':
      if (element.imageId && element.imageType) {
        // Determine variant based on image type with sensible defaults
        let variant: string = 'card';

        if (element.imageType === 'portrait') {
          variant = element.imageVariant && typeof element.imageVariant === 'string' && isPortraitVariant(element.imageVariant)
            ? element.imageVariant
            : DEFAULT_PORTRAIT_VARIANT;
        } else if (element.imageType === 'icon') {
          variant = element.imageVariant && typeof element.imageVariant === 'string' && isIconVariant(element.imageVariant)
            ? element.imageVariant
            : DEFAULT_ICON_VARIANT;
        } else if (typeof element.imageVariant === 'string' && element.imageVariant.length > 0) {
          variant = element.imageVariant;
        }

        const imageUrl = generateImageUrl(element.imageId, element.imageType, variant);
        const altText = element.imageId.replace(/_/g, ' ').replace(/:/g, ' - ');
        
        // Add specific classes based on image type
        let imageClass = 'rich-text-image';
        if (element.imageType === 'portrait') {
          imageClass += ` rich-text-portrait rich-text-portrait-${variant}`;
        } else if (element.imageType === 'icon') {
          imageClass += ' rich-text-icon';
          // Add 'large' class if variant is 'large'
          if (variant === 'large') {
            imageClass += ' large';
          }
        }
        
        return `<img src="${imageUrl}" alt="${escapeHtml(altText)}" class="${imageClass}" />`;
      }
      return '';

    case 'adventure-list':
      // Adventure-list should not be rendered in string renderer
      // It requires React components and API calls
      console.warn('renderElement: adventure-list elements should be handled by RichTextRenderer component');
      return '';

    case 'adventure':
      return escapeHtml(`[ADVENTURE: ${element.adventureId ?? ''}]`);

    case 'inline-option':
      // Inline options should not be rendered directly in rich text
      // They are handled by the InlineOption React component
      // This case should not be reached in normal rendering
      console.warn('renderElement: inline-option elements should be handled by InlineOption component');
      return '';

    case 'divider':
      return '<hr class="rich-text-divider" />';

    case 'code-block': {
      const code =
        typeof element.content === 'string' ? escapeHtml(element.content) : '';
      const language =
        typeof (element as { codeBlockLanguage?: string }).codeBlockLanguage === 'string'
          ? escapeHtml((element as { codeBlockLanguage?: string }).codeBlockLanguage as string)
          : '';
      const langAttr = language ? ` data-language="${language}"` : '';
      const classAttr = language ? ` class="language-${language}"` : '';
      return `<pre class="rich-text-code-block"${langAttr}><code${classAttr}>${code}</code></pre>`;
    }

    case 'conditional': {
      const branch =
        Array.isArray(element.children) && element.children.length > 0
          ? element.children
          : Array.isArray(element.elseChildren)
            ? element.elseChildren
            : [];

      if (branch.length === 0) {
        return '';
      }

      return branch.map(renderElement).join('');
    }

    default:
      // Fallback for unknown element types
      const fallbackContent = Array.isArray(element.content) 
        ? element.content.map(renderElement).join('')
        : escapeHtml(element.content as string);
      return fallbackContent;
  }
};

/**
 * Escapes HTML special characters to prevent XSS
 * @param text - Text to escape
 * @returns Escaped HTML string
 */
const escapeHtml = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }
  
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
    // Note: Removed apostrophe escaping as it causes display issues in React components
};

/**
 * Converts a plain text description to a basic rich text structure
 * This is used for backward compatibility with string descriptions
 * @param text - Plain text description
 * @returns Basic rich text structure
 */
export const plainTextToRichText = (text: string): RichTextDescription => {
  if (!text || text.trim() === '') {
    return { elements: [] };
  }

  // Sanitize the input text to prevent XSS
  const sanitizedText = sanitizeHtml(text);
  
  // Split by double newlines to create paragraphs
  const paragraphs = sanitizedText.split(/\n\s*\n/).filter(p => p.trim() !== '');
  
  const elements: RichTextElement[] = paragraphs.map(paragraph => ({
    type: 'paragraph',
    content: paragraph.trim()
  }));

  return { elements };
};

/**
 * Checks if a description is a rich text description
 * @param description - Description to check
 * @returns True if it's a rich text description
 */
export const isRichTextDescription = (description: string | RichTextDescription): description is RichTextDescription => {
  return typeof description === 'object' && description !== null && 'elements' in description;
};

/**
 * Generates the appropriate image URL based on image type and variant
 * @param imageId - The ID of the image
 * @param imageType - The type of image (location-image, portrait, title-image, banner, icon)
 * @param variant - The variant size (thumbnail, card, large, small)
 * @returns The image URL
 */
import { imageServerConfig } from '../config/imageServer';

const generateImageUrl = (imageId: string, imageType: string, variant: string): string => {
  // Use imageServerConfig.baseUrl which handles localhost replacement for network access
  const baseUrl = imageServerConfig.baseUrl;
  
  // Escape the imageId to prevent XSS in URLs
  const escapedImageId = encodeURIComponent(imageId);
  
  switch (imageType) {
    case 'location-image':
      return `${baseUrl}/location-images/${escapedImageId}/${variant}`;
    case 'portrait':
      return `${baseUrl}/portraits/${escapedImageId}/${variant}`;
    case 'title-image':
      return `${baseUrl}/title-images/${escapedImageId}/${variant}`;
    case 'banner':
      return `${baseUrl}/banners/${escapedImageId}/${variant}`;
    case 'icon':
      return `${baseUrl}/icons/${escapedImageId}/${variant}`;
    default:
      // Fallback to generic image
      return `${baseUrl}/portraits/generic/${variant}`;
  }
};
